import { Control, useWatch } from 'react-hook-form';

import { FieldGroup } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { OrderField } from './OrderFormFields';
import { Separator } from '@/components/ui/separator';
import i18n from '@/utils/i18n';
import { OrderFormValues } from '../schema';
import { SectionCard } from '../../components/SectionCard';
import {
  billingCopy,
  FIELD_SETS,
  fieldSetOf,
  type FieldSet,
} from '@vytal/domain/legal-forms';

export function InfoFieldsSection({
  formControl,
}: {
  formControl: Control<OrderFormValues>;
}) {
  const businessType = useWatch({
    control: formControl,
    name: 'businessType',
  });

  const fieldSet = businessType ? fieldSetOf(businessType) : null;

  return (
    <SectionCard
      title={
        fieldSet ? i18n.t('Billing:billingInfo') : i18n.t('Event:generalInfo')
      }
    >
      {!fieldSet && <GeneralInfoFields />}
      {fieldSet && <BillingInfoFields fieldSet={fieldSet} />}
    </SectionCard>
  );
}

function GeneralInfoFields() {
  return (
    <form onSubmit={(ev) => ev.preventDefault()}>
      <FieldGroup>
        <OrderField.BusinessType className="tw:md:w-[calc(50%-8px)]" />
      </FieldGroup>
    </form>
  );
}

function BillingInfoFields({ fieldSet }: { fieldSet: FieldSet }) {
  return (
    <form onSubmit={(ev) => ev.preventDefault()}>
      <FieldGroup className="tw:gap-4">
        <OrderField.BusinessType className="tw:md:w-[calc(50%-8px)]" />

        {fieldSet === 'privatePerson' && <FieldsForPrivatePerson />}

        {fieldSet === 'partnership' && <FieldsForPartnership />}

        {fieldSet === 'corporation' && <FieldsForCorporation />}
      </FieldGroup>
    </form>
  );
}

function FieldsForPrivatePerson() {
  const { labels, addressHeading } = FIELD_SETS.privatePerson;
  return (
    <>
      <GridRow>
        <OrderField.FirstName
          label={billingCopy(labels.firstName, i18n.language)}
        />
        <OrderField.LastName
          label={billingCopy(labels.lastName, i18n.language)}
        />
      </GridRow>

      <GridRow>
        <OrderField.CompanyName />
        <OrderField.Email label={billingCopy(labels.email, i18n.language)} />
      </GridRow>

      <GridRow>
        <OrderField.PhoneNumber
          label={billingCopy(labels.phoneNumber, i18n.language)}
        />
        <OrderField.DateOfBirth className="tw:md:w-[160px]" />
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-1">
        {billingCopy(addressHeading, i18n.language)}
      </span>

      <GridRow>
        <OrderField.AddressStreet />
        <OrderField.AddressExtra />
      </GridRow>

      <GridRow className="tw:md:grid-cols-10">
        <OrderField.AddressZip className="tw:col-span-2" />
        <OrderField.AddressCity className="tw:col-span-3" />
        <OrderField.AddressCountry className="tw:col-span-2" />
        <OrderField.AddressState className="tw:col-span-3" />
      </GridRow>
    </>
  );
}

function FieldsForPartnership() {
  const { labels, addressHeading, representatives } = FIELD_SETS.partnership;
  return (
    <>
      <GridRow>
        <OrderField.FirstName
          label={billingCopy(labels.firstName, i18n.language)}
        />
        <OrderField.LastName
          label={billingCopy(labels.lastName, i18n.language)}
        />
      </GridRow>

      <GridRow>
        <OrderField.CompanyName />
        <OrderField.Email label={billingCopy(labels.email, i18n.language)} />
      </GridRow>

      <GridRow>
        <OrderField.PhoneNumber
          label={billingCopy(labels.phoneNumber, i18n.language)}
        />
        <OrderField.VatId />
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-1">
        {billingCopy(addressHeading, i18n.language)}
      </span>

      <GridRow>
        <OrderField.AddressStreet />
        <OrderField.AddressExtra />
      </GridRow>

      <GridRow>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressZip />
          <OrderField.AddressCity />
        </div>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressCountry inputClassName="tw:max-w-[8rem]" />
          <OrderField.AddressState />
        </div>
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-2">
        {billingCopy(representatives, i18n.language)}
      </span>

      <div>
        <span className="tw:text-xs tw:text-neutral-900">
          {billingCopy(`${representatives}Instructions`, i18n.language)}
          <br />
        </span>
        <span className="tw:text-xs tw:text-neutral-900 tw:font-medium">
          {billingCopy(`${representatives}Example`, i18n.language)}
        </span>
        <OrderField.CorporateStructureInfo
          className="tw:mt-2"
          inputClassName="tw:text-sm"
          placeholder={billingCopy(
            `${representatives}Placeholder`,
            i18n.language,
          )}
        />
      </div>
    </>
  );
}

function FieldsForCorporation() {
  const { labels, addressHeading, representatives } = FIELD_SETS.corporation;
  return (
    <>
      <GridRow>
        <OrderField.FirstName
          label={billingCopy(labels.firstName, i18n.language)}
        />
        <OrderField.LastName
          label={billingCopy(labels.lastName, i18n.language)}
        />
      </GridRow>

      <GridRow>
        <OrderField.CompanyName />
        <OrderField.Email label={billingCopy(labels.email, i18n.language)} />
      </GridRow>

      <GridRow>
        <OrderField.PhoneNumber
          label={billingCopy(labels.phoneNumber, i18n.language)}
        />
        <OrderField.VatId />
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5">
        {billingCopy(addressHeading, i18n.language)}
      </span>

      <GridRow>
        <OrderField.AddressStreet />
        <OrderField.AddressExtra />
      </GridRow>

      <GridRow>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressZip />
          <OrderField.AddressCity />
        </div>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressCountry inputClassName="tw:md:max-w-[13rem] tw:max-w-[10rem]" />
          <OrderField.AddressState />
        </div>
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-2">
        {billingCopy(representatives, i18n.language)}
      </span>

      <div>
        <span className="tw:text-xs tw:text-neutral-900">
          {billingCopy(`${representatives}Instructions`, i18n.language)}
          <br />
        </span>
        <span className="tw:text-xs tw:text-neutral-900 tw:font-medium">
          {billingCopy(`${representatives}Example`, i18n.language)}
        </span>
        <OrderField.CorporateStructureInfo
          className="tw:mt-2"
          inputClassName="tw:text-sm"
          placeholder={billingCopy(
            `${representatives}Placeholder`,
            i18n.language,
          )}
        />
      </div>
    </>
  );
}

function GridRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:md:grid tw:md:grid-cols-2 tw:gap-4',
        className,
      )}
    >
      {children}
    </div>
  );
}
